import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Stack } from '@mui/system';
import { Maintenance, WhosOnline } from '../components/room/types';
import {
  uniqueNamesGenerator,
  adjectives,
  animals,
} from 'unique-names-generator';
import { getStartOfDayPacific } from '../components/room/utils';
import { Profile } from '@par/shared/types/player';

const nameGenConfig = {
  dictionaries: [adjectives, animals],
  separator: ' ',
  length: 2,
};

const GHOST_CHALLENGES_PER_DAY = 3;

export type GhostChallengConfig = {
  categorySet: string;
  customGameName: string;
  gameType: 'casual' | 'pro';
  ghostChallenge?: boolean;
  ghostChallengeFrom?: string;
  ghostChallengeTo?: string;
  ranked: boolean;
};

type GhostChallengeSettings = Pick<
  Profile['ghostChallengeSettings'],
  'category' | 'difficulty' | 'ranked'
>;

type PropTypes = {
  close: () => void;
  ghostChallengesSentToToday: string[];
  ghostChallengeSettings: GhostChallengeSettings;
  ghostChallengeTo?: { profileId: string; username: string };
  isAdmin: boolean;
  lastGhostChallengeSentAt: number;
  maintenance: Maintenance;
  open: boolean;
  profileId: string;
  submitPlayGhostChallenge: (config: GhostChallengConfig) => void;
};

export const GhostChallengeDialog = ({
  close,
  ghostChallengesSentToToday,
  ghostChallengeSettings,
  ghostChallengeTo,
  isAdmin,
  lastGhostChallengeSentAt,
  maintenance,
  open,
  profileId,
  submitPlayGhostChallenge,
}: PropTypes) => {
  const [ranked, setRanked] = useState<string | null>('friendly');
  const [gameType, setGameType] = useState<'casual' | 'pro'>('casual');
  const [categorySet, setCategorySet] = useState('all');
  const [customGameName, setCustomGameName] = useState('');
  const theme = useTheme();

  useEffect(() => {
    if (!open) return;
    const generatedcustomGameName = uniqueNamesGenerator({
      ...nameGenConfig,
      style: 'capital',
    });
    setCustomGameName(generatedcustomGameName);
    if (Object.keys(ghostChallengeSettings).length > 0) {
      const { category, difficulty, ranked } = ghostChallengeSettings;
      setCategorySet(category);
      setGameType(difficulty);
      setRanked(ranked ? 'ranked' : 'friendly');
    }
  }, [open]);

  const handleRankedChange = (
    event: React.MouseEvent<HTMLElement>,
    change: string | null
  ) => {
    if (change !== null) {
      setRanked(change);
      if (change === 'ranked') {
        setGameType('pro');
        setCategorySet('all');
      }
    }
  };

  const handleGameTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    change: 'casual' | 'pro' | null
  ) => {
    if (change !== null) setGameType(change);
  };

  const handleCategorySetChange = (
    event: React.MouseEvent<HTMLElement>,
    change: string | null
  ) => {
    if (change !== null) setCategorySet(change);
  };

  const handlePlayGhostChallengeClick = () => {
    const config: GhostChallengConfig = {
      categorySet,
      customGameName,
      gameType,
      ghostChallenge: true,
      ghostChallengeFrom: profileId,
      ghostChallengeTo: ghostChallengeTo?.profileId,
      ranked: ranked === 'ranked',
    };
    submitPlayGhostChallenge(config);
  };

  const handleCustomNameOnChange = (value: string) => {
    if (value.length > 100) return;
    setCustomGameName(value);
  };

  const rankedTooltip = () => (
    <Typography variant="caption">
      Your MMR will update based on your MMR at the time the game is completed
    </Typography>
  );

  const ghostChallengesRemaining = () => {
    if (
      // !isAdmin &&
      lastGhostChallengeSentAt &&
      lastGhostChallengeSentAt > getStartOfDayPacific() &&
      ghostChallengesSentToToday &&
      ghostChallengesSentToToday.length >= GHOST_CHALLENGES_PER_DAY
    ) {
      return 0;
    }
    if (
      lastGhostChallengeSentAt &&
      lastGhostChallengeSentAt < getStartOfDayPacific() &&
      Date.now() >= getStartOfDayPacific()
    ) {
      return 3;
    }
    return GHOST_CHALLENGES_PER_DAY - ghostChallengesSentToToday.length;
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md">
      <DialogTitle>
        <Stack spacing={2}>
          <Typography
            variant="h4"
            textAlign={'center'}
            sx={{
              textShadow:
                theme.palette.mode === 'dark'
                  ? `0 0 0px ${theme.palette.dailies.main}, 0 0 0px ${theme.palette.dailies.main}, 0px 0px 16px ${theme.palette.dailies.main}`
                  : 'none',
            }}
          >
            Ghost Challenge
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            sx={{ display: 'flex', justifyContent: 'center' }}
          >
            <Typography variant="h6" textAlign={'center'}>
              Send
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {ghostChallengeTo?.username}
            </Typography>
            <Typography variant="h6" textAlign={'center'}>
              a match!
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{ display: 'flex', justifyContent: 'center' }}
          >
            <Typography variant="caption">You have</Typography>
            <Typography variant="caption" fontWeight={600}>
              {ghostChallengesRemaining()}
            </Typography>
            <Typography variant="caption">
              Ghost Challenges remaining today
            </Typography>
          </Stack>
          {ghostChallengesRemaining() === 0 ? (
            <Typography variant="caption" textAlign={'center'}>
              *Ghost Challenges will replenish at midnight PST
            </Typography>
          ) : null}
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ width: '100%', height: '100%' }}>
        <Stack
          spacing={4}
          sx={{ marginTop: 2, display: 'flex', alignItems: 'center' }}
        >
          <TextField
            sx={{ width: '50%' }}
            id="customGameName"
            label="Game Name (optional)"
            variant="standard"
            value={customGameName}
            onChange={(e) => handleCustomNameOnChange(e.target.value)}
          />

          <ToggleButtonGroup
            color="primary"
            value={ranked}
            exclusive
            onChange={handleRankedChange}
          >
            <ToggleButton value="friendly" sx={{ minWidth: 150 }}>
              Friendly
            </ToggleButton>
            <ToggleButton value="ranked" sx={{ minWidth: 150 }}>
              <Tooltip title={rankedTooltip()} placement="top">
                <Typography
                  fontSize={14}
                  sx={{ color: theme.palette.ranked.main }}
                >
                  Ranked
                </Typography>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            color="primary"
            value={gameType}
            exclusive
            onChange={handleGameTypeChange}
          >
            <ToggleButton
              value="casual"
              sx={{ minWidth: 150 }}
              disabled={ranked === 'ranked'}
            >
              Casual
            </ToggleButton>
            <ToggleButton value="pro" sx={{ minWidth: 150 }}>
              Pro
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            color="primary"
            value={categorySet}
            exclusive
            onChange={handleCategorySetChange}
          >
            <ToggleButton value="all" sx={{ minWidth: 150 }}>
              All
            </ToggleButton>
            <ToggleButton
              value="popCulture"
              sx={{ minWidth: 150 }}
              disabled={ranked === 'ranked'}
            >
              Pop Culture
            </ToggleButton>
            <ToggleButton
              value="humanities"
              sx={{ minWidth: 150 }}
              disabled={ranked === 'ranked'}
            >
              Humanities
            </ToggleButton>
            <ToggleButton
              value="hardSoftSciences"
              sx={{ minWidth: 150 }}
              disabled={ranked === 'ranked'}
            >
              Hard & Soft Sciences
            </ToggleButton>
            <ToggleButton
              value="fineArts"
              sx={{ minWidth: 150 }}
              disabled={ranked === 'ranked'}
            >
              Fine Arts
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" size="large" onClick={close} fullWidth>
          CANCEL
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={handlePlayGhostChallengeClick}
          fullWidth
          disabled={ghostChallengesRemaining() === 0 || maintenance.scheduled}
        >
          {ghostChallengesRemaining() === 0
            ? 'LIMIT REACHED!'
            : 'PLAY YOUR MATCH!'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
